import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  Plus,
  Trash2
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import complianceRuleMasterService from '../../services/complianceRuleMasterService'
import contractTypeMasterService from '../../services/contractTypeMasterService'

const ComplianceRuleMasterForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isEditMode = location.pathname.includes('/edit')
  const isViewMode = location.pathname.includes('/view')
  const ruleData = location.state?.ruleData

  const [formData, setFormData] = useState({
    complianceRuleCode: '',
    complianceRuleName: '',
    complianceRuleDescription: '',
    category: 'Regulatory',
    regulatoryAuthority: '',
    jurisdiction: '',
    applicableLaws: [],
    requirements: [],
    applicableContractTypes: [],
    complianceChecks: [],
    auditRequirements: {
      requiresAudit: false,
      auditFrequency: 'Annually',
      auditScope: '',
      retentionPeriod: 7
    },
    penalties: {
      nonCompliancePenalty: '',
      penaltyAmount: 0,
      penaltyCurrency: 'INR',
      consequences: ''
    },
    status: 'Active',
    effectiveDate: '',
    expiryDate: '',
    priority: 'Medium',
    notes: ''
  })

  const [contractTypes, setContractTypes] = useState([])
  const [isLoadingRuleCode, setIsLoadingRuleCode] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [newLaw, setNewLaw] = useState({ lawName: '', lawCode: '', effectiveDate: '', expiryDate: '' })
  const [newRequirement, setNewRequirement] = useState({ requirement: '', requirementType: 'Mandatory', description: '' })
  const [newCheck, setNewCheck] = useState({ checkName: '', checkType: 'Document Review', isRequired: true, checkFrequency: 'Per Contract' })

  // Fetch next compliance rule code (only for new rules)
  useEffect(() => {
    if (!isEditMode && !isViewMode) {
      const fetchRuleCode = async () => {
        try {
          setIsLoadingRuleCode(true)
          const response = await complianceRuleMasterService.getNextComplianceRuleCode()
          if (response.success && response.data) {
            setFormData(prev => ({
              ...prev,
              complianceRuleCode: response.data.complianceRuleCode || response.data.formattedValue || 'CR0001'
            }))
          }
        } catch (error) {
          console.error('Error fetching compliance rule code:', error)
          setFormData(prev => ({ ...prev, complianceRuleCode: 'CR0001' }))
        } finally {
          setIsLoadingRuleCode(false)
        }
      }
      fetchRuleCode()
    }
  }, [isEditMode, isViewMode])

  // Load contract types
  useEffect(() => {
    loadContractTypes()
  }, [])

  // Load compliance rule data if editing or viewing
  useEffect(() => {
    if ((isEditMode || isViewMode) && ruleData?._id) {
      loadRuleData(ruleData._id)
    }
  }, [isEditMode, isViewMode, ruleData])

  const loadContractTypes = async () => {
    try {
      const response = await contractTypeMasterService.getAllContractTypes({ status: 'Active', limit: 1000 })
      if (response.success) {
        setContractTypes(response.data || [])
      }
    } catch (error) {
      console.error('Error loading contract types:', error)
    }
  }

  const loadRuleData = async (id) => {
    try {
      setIsLoading(true)
      const response = await complianceRuleMasterService.getComplianceRuleById(id)
      if (response.success && response.data) {
        const rule = response.data
        setFormData({
          complianceRuleCode: rule.complianceRuleCode || '',
          complianceRuleName: rule.complianceRuleName || '',
          complianceRuleDescription: rule.complianceRuleDescription || '',
          category: rule.category || 'Regulatory',
          regulatoryAuthority: rule.regulatoryAuthority || '',
          jurisdiction: rule.jurisdiction || '',
          applicableLaws: rule.applicableLaws || [],
          requirements: rule.requirements || [],
          applicableContractTypes: rule.applicableContractTypes?.map(ct => ct._id || ct) || [],
          complianceChecks: rule.complianceChecks || [],
          auditRequirements: rule.auditRequirements || {
            requiresAudit: false,
            auditFrequency: 'Annually',
            auditScope: '',
            retentionPeriod: 7
          },
          penalties: rule.penalties || {
            nonCompliancePenalty: '',
            penaltyAmount: 0,
            penaltyCurrency: 'INR',
            consequences: ''
          },
          status: rule.status || 'Active',
          effectiveDate: rule.effectiveDate ? new Date(rule.effectiveDate).toISOString().split('T')[0] : '',
          expiryDate: rule.expiryDate ? new Date(rule.expiryDate).toISOString().split('T')[0] : '',
          priority: rule.priority || 'Medium',
          notes: rule.notes || ''
        })
      }
    } catch (error) {
      console.error('Error loading compliance rule data:', error)
      toast.error('Failed to load compliance rule data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    
    if (name.startsWith('auditRequirements.')) {
      const auditField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        auditRequirements: {
          ...prev.auditRequirements,
          [auditField]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
        }
      }))
    } else if (name.startsWith('penalties.')) {
      const penaltyField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        penalties: {
          ...prev.penalties,
          [penaltyField]: type === 'number' ? parseFloat(value) || 0 : value
        }
      }))
    } else if (name === 'applicableContractTypes') {
      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value)
      setFormData(prev => ({
        ...prev,
        applicableContractTypes: selectedOptions
      }))
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const addLaw = () => {
    if (!newLaw.lawName.trim()) {
      toast.error('Please enter a law name')
      return
    }
    
    setFormData(prev => ({
      ...prev,
      applicableLaws: [...prev.applicableLaws, { ...newLaw }]
    }))
    
    setNewLaw({ lawName: '', lawCode: '', effectiveDate: '', expiryDate: '' })
  }

  const removeLaw = (index) => {
    setFormData(prev => ({
      ...prev,
      applicableLaws: prev.applicableLaws.filter((_, i) => i !== index)
    }))
  }

  const addRequirement = () => {
    if (!newRequirement.requirement.trim()) {
      toast.error('Please enter a requirement')
      return
    }
    
    setFormData(prev => ({
      ...prev,
      requirements: [...prev.requirements, { ...newRequirement }]
    }))
    
    setNewRequirement({ requirement: '', requirementType: 'Mandatory', description: '' })
  }

  const removeRequirement = (index) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index)
    }))
  }

  const addCheck = () => {
    if (!newCheck.checkName.trim()) {
      toast.error('Please enter a check name')
      return
    }
    
    setFormData(prev => ({
      ...prev,
      complianceChecks: [...prev.complianceChecks, { ...newCheck }]
    }))
    
    setNewCheck({ checkName: '', checkType: 'Document Review', isRequired: true, checkFrequency: 'Per Contract' })
  }

  const removeCheck = (index) => {
    setFormData(prev => ({
      ...prev,
      complianceChecks: prev.complianceChecks.filter((_, i) => i !== index)
    }))
  }

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.complianceRuleName) {
        toast.error('Please fill in all required fields (Compliance Rule Name)')
        return
      }

      setIsLoading(true)
      
      // Convert date strings to Date objects
      const submitData = {
        ...formData,
        effectiveDate: formData.effectiveDate ? new Date(formData.effectiveDate) : null,
        expiryDate: formData.expiryDate ? new Date(formData.expiryDate) : null,
        applicableLaws: formData.applicableLaws.map(law => ({
          ...law,
          effectiveDate: law.effectiveDate ? new Date(law.effectiveDate) : null,
          expiryDate: law.expiryDate ? new Date(law.expiryDate) : null
        }))
      }
      
      if (isEditMode && ruleData?._id) {
        const response = await complianceRuleMasterService.updateComplianceRule(ruleData._id, submitData)
        if (response.success) {
          toast.success('Compliance rule updated successfully!')
          navigate('/contracts/compliance-rule-master')
        } else {
          toast.error(response.message || 'Failed to update compliance rule')
        }
      } else {
        const response = await complianceRuleMasterService.createComplianceRule(submitData)
        if (response.success) {
          toast.success('Compliance rule created successfully!')
          navigate('/contracts/compliance-rule-master')
        } else {
          toast.error(response.message || 'Failed to create compliance rule')
        }
      }
    } catch (error) {
      console.error('Error saving compliance rule:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to save compliance rule')
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if (isEditMode || isViewMode) {
      loadRuleData(ruleData._id)
    } else {
      setFormData(prev => ({
        ...prev,
        complianceRuleName: '',
        complianceRuleDescription: '',
        category: 'Regulatory',
        regulatoryAuthority: '',
        jurisdiction: '',
        applicableLaws: [],
        requirements: [],
        applicableContractTypes: [],
        complianceChecks: [],
        auditRequirements: {
          requiresAudit: false,
          auditFrequency: 'Annually',
          auditScope: '',
          retentionPeriod: 7
        },
        penalties: {
          nonCompliancePenalty: '',
          penaltyAmount: 0,
          penaltyCurrency: 'INR',
          consequences: ''
        },
        status: 'Active',
        effectiveDate: '',
        expiryDate: '',
        priority: 'Medium',
        notes: ''
      }))
      setNewLaw({ lawName: '', lawCode: '', effectiveDate: '', expiryDate: '' })
      setNewRequirement({ requirement: '', requirementType: 'Mandatory', description: '' })
      setNewCheck({ checkName: '', checkType: 'Document Review', isRequired: true, checkFrequency: 'Per Contract' })
    }
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Contracts', path: '/contracts' },
    { label: 'Compliance Rules', path: '/contracts/compliance-rule-master' },
    { 
      label: isEditMode ? 'Edit Compliance Rule' : isViewMode ? 'View Compliance Rule' : 'New Compliance Rule', 
      path: location.pathname, 
      isCurrent: true 
    }
  ]

  if (isLoading && (isEditMode || isViewMode)) {
    return (
      <div className="sales-dashboard">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading compliance rule...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Compliance Rule' : isViewMode ? 'View Compliance Rule' : 'New Compliance Rule'}</h1>
          <p>{isEditMode ? 'Update compliance rule' : isViewMode ? 'View compliance rule' : 'Create a new compliance rule'}</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/contracts/compliance-rule-master')}
            style={{
              padding: '10px 20px',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              color: '#495057'
            }}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Compliance Rule Master Entry</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header">
            <h3>Rule Information</h3>
          </div>
          {/* Row 1: Rule Code, Rule Name, Category, Status */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="complianceRuleCode" className="form-label required">
                Rule Code
              </label>
              <input
                type="text"
                id="complianceRuleCode"
                name="complianceRuleCode"
                value={isLoadingRuleCode ? 'Generating...' : formData.complianceRuleCode}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="complianceRuleName" className="form-label required">
                Rule Name
              </label>
              <input
                type="text"
                id="complianceRuleName"
                name="complianceRuleName"
                value={formData.complianceRuleName}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                required
                placeholder="e.g., GDPR Data Protection"
              />
            </div>

            <div className="form-group">
              <label htmlFor="category" className="form-label">
                Category
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Regulatory">Regulatory</option>
                <option value="Legal">Legal</option>
                <option value="Financial">Financial</option>
                <option value="Data Protection">Data Protection</option>
                <option value="Industry Specific">Industry Specific</option>
                <option value="Internal Policy">Internal Policy</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Superseded">Superseded</option>
                <option value="Archived">Archived</option>
              </select>
            </div>
          </div>

          {/* Row 2: Priority, Regulatory Authority, Jurisdiction */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="priority" className="form-label">
                Priority
              </label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="regulatoryAuthority" className="form-label">
                Regulatory Authority
              </label>
              <input
                type="text"
                id="regulatoryAuthority"
                name="regulatoryAuthority"
                value={formData.regulatoryAuthority}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="e.g., SEBI, RBI"
              />
            </div>

            <div className="form-group">
              <label htmlFor="jurisdiction" className="form-label">
                Jurisdiction
              </label>
              <input
                type="text"
                id="jurisdiction"
                name="jurisdiction"
                value={formData.jurisdiction}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="e.g., India, EU"
              />
            </div>

            <div className="form-group">
              <label htmlFor="effectiveDate" className="form-label">
                Effective Date
              </label>
              <input
                type="date"
                id="effectiveDate"
                name="effectiveDate"
                value={formData.effectiveDate}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              />
            </div>
          </div>

          {/* Expiry Date */}
          <div className="form-group">
            <label htmlFor="expiryDate" className="form-label">
              Expiry Date
            </label>
            <input
              type="date"
              id="expiryDate"
              name="expiryDate"
              value={formData.expiryDate}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
            />
          </div>

          {/* Rule Description */}
          <div className="form-group">
            <label htmlFor="complianceRuleDescription" className="form-label">
              Description
            </label>
            <textarea
              id="complianceRuleDescription"
              name="complianceRuleDescription"
              value={formData.complianceRuleDescription}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Brief description of the compliance rule..."
            />
          </div>

          {/* Applicable Laws */}
          <div className="section-header">
            <h3>Applicable Laws</h3>
          </div>

          {formData.applicableLaws.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              {formData.applicableLaws.map((law, index) => (
                <div key={index} style={{ 
                  marginBottom: '12px',
                  padding: '12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  background: '#f8f9fa'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <strong>{law.lawName}</strong>
                    {!isViewMode && (
                      <button
                        type="button"
                        onClick={() => removeLaw(index)}
                        style={{
                          padding: '4px 8px',
                          border: 'none',
                          background: '#dc3545',
                          color: '#fff',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  {law.lawCode && <div style={{ fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)' }}>Code: {law.lawCode}</div>}
                  {law.effectiveDate && <div style={{ fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)' }}>Effective: {new Date(law.effectiveDate).toLocaleDateString()}</div>}
                </div>
              ))}
            </div>
          )}

          {!isViewMode && (
            <div style={{ marginBottom: '16px', padding: '16px', border: '1px solid #e9ecef', borderRadius: '6px' }}>
              <div className="grid-row-4" style={{ marginBottom: '12px' }}>
                <div className="form-group">
                  <label className="form-label">Law Name</label>
                  <input
                    type="text"
                    value={newLaw.lawName}
                    onChange={(e) => setNewLaw({ ...newLaw, lawName: e.target.value })}
                    className="form-input"
                    placeholder="e.g., Companies Act 2013"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Law Code</label>
                  <input
                    type="text"
                    value={newLaw.lawCode}
                    onChange={(e) => setNewLaw({ ...newLaw, lawCode: e.target.value })}
                    className="form-input"
                    placeholder="e.g., CA2013"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Effective Date</label>
                  <input
                    type="date"
                    value={newLaw.effectiveDate}
                    onChange={(e) => setNewLaw({ ...newLaw, effectiveDate: e.target.value })}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Expiry Date</label>
                  <input
                    type="date"
                    value={newLaw.expiryDate}
                    onChange={(e) => setNewLaw({ ...newLaw, expiryDate: e.target.value })}
                    className="form-input"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={addLaw}
                style={{
                  padding: '10px 16px',
                  border: 'none',
                  background: '#292752',
                  color: '#fff',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Plus size={16} />
                Add Law
              </button>
            </div>
          )}

          {/* Requirements */}
          <div className="section-header">
            <h3>Requirements</h3>
          </div>

          {formData.requirements.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              {formData.requirements.map((req, index) => (
                <div key={index} style={{ 
                  marginBottom: '12px',
                  padding: '12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  background: '#f8f9fa'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div>
                      <strong>{req.requirement}</strong>
                      <span className={`status-badge ${req.requirementType === 'Mandatory' ? 'danger' : req.requirementType === 'Recommended' ? 'warning' : 'inactive'}`} style={{ marginLeft: '8px', fontSize: '12px' }}>
                        {req.requirementType}
                      </span>
                    </div>
                    {!isViewMode && (
                      <button
                        type="button"
                        onClick={() => removeRequirement(index)}
                        style={{
                          padding: '4px 8px',
                          border: 'none',
                          background: '#dc3545',
                          color: '#fff',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  {req.description && <div style={{ fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)' }}>{req.description}</div>}
                </div>
              ))}
            </div>
          )}

          {!isViewMode && (
            <div style={{ marginBottom: '16px', padding: '16px', border: '1px solid #e9ecef', borderRadius: '6px' }}>
              <div className="grid-row-4" style={{ marginBottom: '12px' }}>
                <div className="form-group">
                  <label className="form-label">Requirement</label>
                  <input
                    type="text"
                    value={newRequirement.requirement}
                    onChange={(e) => setNewRequirement({ ...newRequirement, requirement: e.target.value })}
                    className="form-input"
                    placeholder="e.g., Data encryption required"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Type</label>
                  <select
                    value={newRequirement.requirementType}
                    onChange={(e) => setNewRequirement({ ...newRequirement, requirementType: e.target.value })}
                    className="form-input"
                  >
                    <option value="Mandatory">Mandatory</option>
                    <option value="Recommended">Recommended</option>
                    <option value="Optional">Optional</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <input
                    type="text"
                    value={newRequirement.description}
                    onChange={(e) => setNewRequirement({ ...newRequirement, description: e.target.value })}
                    className="form-input"
                    placeholder="Optional description"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={addRequirement}
                style={{
                  padding: '10px 16px',
                  border: 'none',
                  background: '#292752',
                  color: '#fff',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Plus size={16} />
                Add Requirement
              </button>
            </div>
          )}

          {/* Compliance Checks */}
          <div className="section-header">
            <h3>Compliance Checks</h3>
          </div>

          {formData.complianceChecks.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              {formData.complianceChecks.map((check, index) => (
                <div key={index} style={{ 
                  marginBottom: '12px',
                  padding: '12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  background: '#f8f9fa'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div>
                      <strong>{check.checkName}</strong>
                      <span style={{ marginLeft: '8px', fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>
                        ({check.checkType} - {check.checkFrequency})
                      </span>
                      {check.isRequired && (
                        <span className="status-badge danger" style={{ marginLeft: '8px', fontSize: '12px' }}>
                          Required
                        </span>
                      )}
                    </div>
                    {!isViewMode && (
                      <button
                        type="button"
                        onClick={() => removeCheck(index)}
                        style={{
                          padding: '4px 8px',
                          border: 'none',
                          background: '#dc3545',
                          color: '#fff',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isViewMode && (
            <div style={{ marginBottom: '16px', padding: '16px', border: '1px solid #e9ecef', borderRadius: '6px' }}>
              <div className="grid-row-4" style={{ marginBottom: '12px' }}>
                <div className="form-group">
                  <label className="form-label">Check Name</label>
                  <input
                    type="text"
                    value={newCheck.checkName}
                    onChange={(e) => setNewCheck({ ...newCheck, checkName: e.target.value })}
                    className="form-input"
                    placeholder="e.g., Document Review"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Check Type</label>
                  <select
                    value={newCheck.checkType}
                    onChange={(e) => setNewCheck({ ...newCheck, checkType: e.target.value })}
                    className="form-input"
                  >
                    <option value="Document Review">Document Review</option>
                    <option value="Legal Review">Legal Review</option>
                    <option value="Financial Review">Financial Review</option>
                    <option value="Technical Review">Technical Review</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Frequency</label>
                  <select
                    value={newCheck.checkFrequency}
                    onChange={(e) => setNewCheck({ ...newCheck, checkFrequency: e.target.value })}
                    className="form-input"
                  >
                    <option value="One-time">One-time</option>
                    <option value="Per Contract">Per Contract</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="Annually">Annually</option>
                    <option value="As Needed">As Needed</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Required</label>
                  <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                    <input
                      type="checkbox"
                      checked={newCheck.isRequired}
                      onChange={(e) => setNewCheck({ ...newCheck, isRequired: e.target.checked })}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <label style={{ marginLeft: '8px', fontSize: '14px', color: '#495057' }}>
                      Required
                    </label>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={addCheck}
                style={{
                  padding: '10px 16px',
                  border: 'none',
                  background: '#292752',
                  color: '#fff',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Plus size={16} />
                Add Check
              </button>
            </div>
          )}

          {/* Audit Requirements */}
          <div className="section-header">
            <h3>Audit Requirements</h3>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="auditRequirements.requiresAudit" className="form-label">
                Requires Audit
              </label>
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                <input
                  type="checkbox"
                  id="auditRequirements.requiresAudit"
                  name="auditRequirements.requiresAudit"
                  checked={formData.auditRequirements.requiresAudit}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={{ width: '18px', height: '18px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
                />
                <label htmlFor="auditRequirements.requiresAudit" style={{ marginLeft: '8px', fontSize: '14px', color: '#495057' }}>
                  Audit required
                </label>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="auditRequirements.auditFrequency" className="form-label">
                Audit Frequency
              </label>
              <select
                id="auditRequirements.auditFrequency"
                name="auditRequirements.auditFrequency"
                value={formData.auditRequirements.auditFrequency}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Semi-Annually">Semi-Annually</option>
                <option value="Annually">Annually</option>
                <option value="As Needed">As Needed</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="auditRequirements.retentionPeriod" className="form-label">
                Retention Period (Years)
              </label>
              <input
                type="number"
                id="auditRequirements.retentionPeriod"
                name="auditRequirements.retentionPeriod"
                value={formData.auditRequirements.retentionPeriod}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="auditRequirements.auditScope" className="form-label">
              Audit Scope
            </label>
            <textarea
              id="auditRequirements.auditScope"
              name="auditRequirements.auditScope"
              value={formData.auditRequirements.auditScope}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="2"
              placeholder="Describe the audit scope..."
            />
          </div>

          {/* Penalties */}
          <div className="section-header">
            <h3>Penalties & Consequences</h3>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="penalties.penaltyAmount" className="form-label">
                Penalty Amount
              </label>
              <input
                type="number"
                id="penalties.penaltyAmount"
                name="penalties.penaltyAmount"
                value={formData.penalties.penaltyAmount}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="penalties.penaltyCurrency" className="form-label">
                Currency
              </label>
              <select
                id="penalties.penaltyCurrency"
                name="penalties.penaltyCurrency"
                value={formData.penalties.penaltyCurrency}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="penalties.nonCompliancePenalty" className="form-label">
              Non-Compliance Penalty Description
            </label>
            <input
              type="text"
              id="penalties.nonCompliancePenalty"
              name="penalties.nonCompliancePenalty"
              value={formData.penalties.nonCompliancePenalty}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              placeholder="e.g., Fine up to 4% of annual revenue"
            />
          </div>

          <div className="form-group">
            <label htmlFor="penalties.consequences" className="form-label">
              Consequences
            </label>
            <textarea
              id="penalties.consequences"
              name="penalties.consequences"
              value={formData.penalties.consequences}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="2"
              placeholder="Describe consequences of non-compliance..."
            />
          </div>

          {/* Applicable Contract Types */}
          <div className="form-group">
            <label htmlFor="applicableContractTypes" className="form-label">
              Applicable Contract Types
            </label>
            <select
              id="applicableContractTypes"
              name="applicableContractTypes"
              value={formData.applicableContractTypes}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              multiple
              style={{ minHeight: '100px' }}
            >
              {contractTypes.map(type => (
                <option key={type._id} value={type._id}>
                  {type.contractTypeName} ({type.contractTypeCode})
                </option>
              ))}
            </select>
            <small style={{ color: 'var(--color-text-secondary, #5B6478)', fontSize: '12px', display: 'block', marginTop: '4px' }}>
              Hold Ctrl/Cmd to select multiple contract types
            </small>
          </div>

          {/* Notes */}
          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Additional notes..."
            />
          </div>

          {/* Action Buttons */}
          {!isViewMode && (
            <div className="form-actions">
              <button
                type="button"
                onClick={handleSave}
                disabled={isLoading}
                className="btn btn-primary"
                style={{
                  padding: '10px 24px',
                  background: '#292752',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isLoading ? 0.6 : 1
                }}
              >
                <Save size={16} />
                {isLoading ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={isLoading}
                className="btn btn-secondary"
                style={{
                  padding: '10px 24px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  background: '#fff',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  color: '#495057'
                }}
              >
                <RotateCcw size={16} />
                Reset
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default ComplianceRuleMasterForm

